import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFileHistoryCandidates, getFileMetadataHistory } from "lib/api/fileHistory";
import { useAppStore } from "lib/store/useAppStore";
import FacilityQrHistory from "pages/qr/FacilityQrHistory";
import { ProjectFileUploadPanel } from "pages/projects/detail/ProjectFileUploadPage";
import "css/facilityQr.css";
import "./fileHistoryPanel.css";

export default function FileHistoryPanel({ file, projectId, userId }) {
  const navigate = useNavigate();
  const showAlert = useAppStore(state => state.showAlert);
  const fetchProjectTree = useAppStore(state => state.fetchProjectTree);
  const [history, setHistory] = useState({ state: "loading", files: [] });
  const [extraIds, setExtraIds] = useState([]);
  const [draftIds, setDraftIds] = useState([]);
  const [revision, setRevision] = useState(0);
  const [showUpload, setShowUpload] = useState(false);
  const [showCandidates, setShowCandidates] = useState(false);
  const [query, setQuery] = useState({ page: 1, search: "" });
  const [search, setSearch] = useState("");
  const [candidates, setCandidates] = useState({ files: [], state: "idle", has_more: false });
  useEffect(() => {
    const controller = new AbortController();
    setHistory({ state: "loading", files: [] });
    getFileMetadataHistory(projectId, file.upload_id, extraIds, controller.signal).then(result => {
      if (!controller.signal.aborted) setHistory(result.success
        ? { ...result.data, state: "ready" } : { state: "error", files: [] });
    }).catch(() => { if (!controller.signal.aborted) setHistory({ state: "error", files: [] }); });
    return () => controller.abort();
  }, [projectId, file.upload_id, extraIds, revision]);
  useEffect(() => {
    if (!showCandidates) return;
    const controller = new AbortController();
    setCandidates(previous => ({ ...previous, state: "loading" }));
    getFileHistoryCandidates(projectId, file.upload_id, { ...query, signal: controller.signal }).then(result => {
      if (!controller.signal.aborted) setCandidates(result.success ? { ...result.data, state: "ready" } : { files: [], state: "error" });
    }).catch(() => { if (!controller.signal.aborted) setCandidates({ files: [], state: "error" }); });
    return () => controller.abort();
  }, [projectId, file.upload_id, query, showCandidates]);
  return <section className="fileHistoryPanel fileTabSection">
    <div className="fileHistoryPanel__heading fileTabHeader">
      <div className="fileTabHeader__copy">
        <h3>측정 이력</h3>
        {history.state === "ready" && <span className="fileTabHeader__status">{history.files.length}개 기록</span>}
        <p>같은 경로·설비 구분의 파일을 비교합니다. 추가한 파일은 현재 비교에만 포함됩니다.</p>
      </div>
      <div className="fileHistoryActions fileHistoryToolbar fileTabHeader__actions">
        <button type="button" className="btn-bg-blue" aria-expanded={showUpload} onClick={() => setShowUpload(value => !value)}>파일 업로드</button>
        <button type="button" aria-expanded={showCandidates} onClick={() => { setDraftIds(extraIds); setShowCandidates(true); }}>비교 파일 추가</button>
        {extraIds.length > 0 && <button type="button" onClick={() => setExtraIds([])}>추가 비교 파일 제외 ({extraIds.length})</button>}
        <button type="button" className="fileHistoryRefresh" disabled={history.state === "loading"} onClick={() => setRevision(value => value + 1)}>새로고침</button>
      </div>
    </div>
    {showUpload && <div className="fileHistoryUpload">
      <button type="button" onClick={() => setShowUpload(false)}>업로드 닫기</button>
      <ProjectFileUploadPanel projectId={projectId} userId={userId} showTitle={false}
        defaultMetadata={{ facilityType: file.facility_type, buildingName: file.building_name, floor: file.floor,
          location: file.location, facilityName: file.facility_name, partName: file.part_name }}
        showAlert={showAlert} fetchProjectTree={fetchProjectTree}
        onUploadSuccess={() => { setShowUpload(false); setRevision(value => value + 1); }} />
    </div>}
    {showCandidates && <div className="fileHistoryCandidates">
      <form onSubmit={event => { event.preventDefault(); setQuery({ page: 1, search }); }}>
        <input aria-label="비교할 파일 검색" placeholder="파일명 또는 설비명" value={search} onChange={event => setSearch(event.target.value)} />
        <button type="submit">검색</button>
      </form>
      {candidates.state === "loading" ? <p role="status">파일을 불러오는 중…</p>
        : candidates.state === "error" ? <p role="alert">파일을 불러오지 못했습니다.</p> : <ul>
        {candidates.files.map(item => <li key={item.upload_id}><label>
          <input type="checkbox" checked={draftIds.includes(item.upload_id)} disabled={!draftIds.includes(item.upload_id) && draftIds.length >= 100}
            onChange={event => setDraftIds(ids => event.target.checked ? [...ids, item.upload_id] : ids.filter(id => id !== item.upload_id))} />
          <span className="fileHistoryCandidateText"><strong>{item.file_name}</strong>
            <small>{[item.building_name, item.floor, item.location, item.facility_name, item.part_name].filter(Boolean).join(" / ")}</small></span>
        </label></li>)}
        {!candidates.files.length && <li>조회된 파일이 없습니다.</li>}
      </ul>}
      <div className="fileHistoryActions">
        <button type="button" disabled={query.page <= 1 || candidates.state === "loading"} onClick={() => setQuery(value => ({ ...value, page: value.page - 1 }))}>이전</button>
        <span>{query.page}</span>
        <button type="button" disabled={!candidates.has_more || candidates.state === "loading"} onClick={() => setQuery(value => ({ ...value, page: value.page + 1 }))}>다음</button>
        <button type="button" className="btn-bg-blue" onClick={() => { setExtraIds(draftIds); setShowCandidates(false); }}>비교에 적용 ({draftIds.length})</button>
        <button type="button" onClick={() => setShowCandidates(false)}>취소</button>
      </div>
    </div>}
    {history.state === "loading" && <p role="status">이력을 불러오는 중…</p>}
    {history.state === "error" && <p role="alert">이력을 불러오지 못했습니다.</p>}
    {history.state === "ready" && <>
      {!history.can_analyze && !extraIds.length && <p>건물·층·위치·설비·부품이 모두 지정되지 않았습니다. 비교할 파일을 직접 선택하세요.</p>}
      {extraIds.length > 0 && <p>수동으로 추가한 파일이 포함되어 있습니다. 같은 설비·부품인지 확인하여 비교하세요.</p>}
      <FacilityQrHistory variant="file" files={history.files} currentFile={file} showAnalysis={history.can_analyze || extraIds.length > 0}
        onOpenFile={item => navigate(`/project/${projectId}/file_${item.file_type === "video" ? "video" : "image"}/${item.upload_id}?tab=history`)} />
    </>}
  </section>;
}
